import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { DealCompanyStageEnumType } from '__generated__/graphql';
import { FC } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';
import EditorFormInput from 'components/QuillEditor/EditorFormInput';

import { Dialog, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

import constants from 'utils/constants';
import stageToReadable from 'utils/enums/stage-to-readable';
import { formatCurrencyToShort } from 'utils/format-helper';

import alIndiaLogo from '../../../../../media/alIndiaLogo.png';

const DEAL_DETAILS_QUERY = gql(`
  query GetDealDetailsForInvitePreview($id: ID!) {
    deal(id: $id) {
      companyInformation {
        memo
        stage
        industries
        company {
          name
        }
      }
      syndicate {
        name
      }
      allocation
      dealTerms {
        minimumInvestment
      }
      totalCarryPercentage
    }
  }
`);

const InviteUsersEmailPreviewDialogButton: FC<{
  open: boolean;
  setOpen: (boolean) => void;
  handleToastOpen: () => void;
  onSubmit: (data?: any) => void;
  formData: any;
  setFormData: (data: any) => void;
  dealId: string;
  loading: boolean;
  error: any;
}> = ({
  open,
  setOpen,
  handleToastOpen,
  onSubmit,
  formData,
  setFormData,
  dealId,
  loading,
  error,
}) => {
  const {
    loading: queryLoading,
    error: queryError,
    data,
    refetch,
  } = useQuery(DEAL_DETAILS_QUERY, {
    variables: {
      id: dealId,
    },
  });

  if (queryLoading) return <LoadingIndicator />;

  if (queryError || !data) return <ErrorMessage error={queryError} refetch={refetch} />;

  const deal = data.deal;

  const handleFormSubmit = data => {
    setFormData(data);
    onSubmit(data);
    handleToastOpen();
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogPanel>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogSubTitle>An example of what your email invite will look like.</DialogSubTitle>
          <div className="border p-4 mb-4">
            <img src={alIndiaLogo} width={150} className="mx-auto mb-8" alt="AL India Logo" />
            <div className="flex flex-row gap-4 mx-auto w-full">
              <img className="h-8 w-8 rounded-full" src={constants.ALT_AVATAR_URL} alt="Logo" />
              <h3>
                <span className="font-bold">{deal.syndicate.name}</span> is investing in{' '}
                <span className="font-bold">{deal.companyInformation.company.name}</span>
              </h3>
            </div>
            {formData?.note && (
              <>
                <h3 className="font-bold text-md mt-12">Message from the GP</h3>
                <div className="ql-container ql-snow">
                  <div className="ql-editor" dangerouslySetInnerHTML={{ __html: formData.note }} />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-2 justify-between mt-8 px-4">
              <p className="font-bold">Stage</p>
              <p>
                {
                  stageToReadable(
                    deal.companyInformation.stage || DealCompanyStageEnumType.PreLaunch
                  ).label
                }
              </p>
              <p className="font-bold">Allocation</p>
              <p>{formatCurrencyToShort(deal.allocation)}</p>
              <p className="font-bold">Min. Investment</p>
              <p>
                {formatCurrencyToShort(
                  formData?.minimumInvestmentAmount || deal.dealTerms?.minimumInvestment
                )}
              </p>
              <p className="font-bold">Carry</p>
              <p>{deal.totalCarryPercentage}%</p>
            </div>
            <div className="mt-8 w-full border bg-slate-800 rounded-md">
              <p className="text-gray-200 text-xs p-2 text-center">View deal and invest</p>
            </div>
          </div>
          <h3 className="text-lg font-semibold leading-6 text-gray-700 mt-12">
            Customise your invitation
          </h3>
          <p className="text-sm text-gray-500">
            You can customise the invitation email by adding a note, minimum investment amount, and
            maximum investment amount.
          </p>
          <div className="space-y-6 my-4">
            <FormPanel
              loading={loading}
              error={error}
              onSubmit={handleFormSubmit}
              submitButtonLabel="Send Invitation"
            >
              <div className="max-h-min col-span-full w-full">
                <FormInput
                  fullWidth
                  type="custom"
                  fieldName="note"
                  label="Note"
                  defaultValue={''}
                  customInput={EditorFormInput}
                  onChange={setFormData}
                />
              </div>

              <FormInput
                type="currency"
                fieldName="minimumInvestmentAmount"
                label="Minimum Investment Amount"
                defaultValue={300000}
                onChange={setFormData}
              />
              <FormInput
                type="currency"
                fieldName="maximumInvestmentAmount"
                label="Maximum Investment Amount"
                defaultValue={1000000}
                onChange={setFormData}
              />
            </FormPanel>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default InviteUsersEmailPreviewDialogButton;
